/**
 * ---------------------------------------------------------------------------------------------------
 * COLLECTION TABS
 *
 * Behaviour for sections/collection-tabs.liquid. This is a self contained custom element so that it
 * does not have to be registered inside theme.js: the browser instantiates it on insertion and on
 * removal, which also covers the theme editor section reloads for free.
 *
 * As well as the tab bar and the scroller arrows, this owns the product item actions (quick buy, quick
 * view and color swatches). Those normally live in the theme's FeaturedCollectionSection, but that
 * instance is not created reliably for this section, which left the quick view modal opening empty
 * because its fetch never ran. The behaviour below mirrors the theme's so the mini cart, the loading
 * bar and the quick view product section all react exactly as they do elsewhere.
 *
 * The element is defined only once, so the section is free to output its own <script> tag on every
 * instance without executing the definition twice.
 * ---------------------------------------------------------------------------------------------------
 */

(function () {
  if (window.customElements.get('collection-tabs')) {
    return;
  }

  class CollectionTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-collection-tabs-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-collection-tabs-panel]'));

      this._onTabBarClick = this._onTabBarClick.bind(this);
      this._onTabBarKeyDown = this._onTabBarKeyDown.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
      this._onColorSwatchChange = this._onColorSwatchChange.bind(this);
      this._refreshArrows = this._refreshArrows.bind(this);
      this._onBlockSelect = this._onBlockSelect.bind(this);

      this.tabBar = this.querySelector('[data-collection-tabs-nav]');

      if (this.tabBar) {
        this.tabBar.addEventListener('click', this._onTabBarClick);
        this.tabBar.addEventListener('keydown', this._onTabBarKeyDown);
      }

      this.addEventListener('click', this._onClick);
      this.addEventListener('submit', this._onSubmit);
      this.addEventListener('change', this._onColorSwatchChange);
      window.addEventListener('resize', this._refreshArrows);
      document.addEventListener('shopify:block:select', this._onBlockSelect);

      this.querySelectorAll('[data-collection-tabs-scroller] .scroller__inner').forEach((scrollerInner) => {
        scrollerInner.addEventListener('scroll', this._refreshArrows, { passive: true });
      });

      this._refreshArrows();
    }

    disconnectedCallback() {
      if (this.tabBar) {
        this.tabBar.removeEventListener('click', this._onTabBarClick);
        this.tabBar.removeEventListener('keydown', this._onTabBarKeyDown);
      }

      this.removeEventListener('click', this._onClick);
      this.removeEventListener('submit', this._onSubmit);
      this.removeEventListener('change', this._onColorSwatchChange);
      window.removeEventListener('resize', this._refreshArrows);
      document.removeEventListener('shopify:block:select', this._onBlockSelect);

      this.querySelectorAll('[data-collection-tabs-scroller] .scroller__inner').forEach((scrollerInner) => {
        scrollerInner.removeEventListener('scroll', this._refreshArrows);
      });
    }

    /**
     * ---------------------------------------------------------------------------------------------------
     * TABS
     * ---------------------------------------------------------------------------------------------------
     */

    selectTab(blockId, { focus = false } = {}) {
      this.tabs.forEach((tab) => {
        const isSelected = tab.getAttribute('data-collection-tabs-tab') === blockId;

        tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        tab.setAttribute('tabindex', isSelected ? '0' : '-1');

        if (isSelected && focus) {
          tab.focus();
        }

        if (isSelected && tab.parentNode.scrollWidth > tab.parentNode.clientWidth) {
          tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel.getAttribute('data-collection-tabs-panel') !== blockId;
      });

      this._refreshArrows();
    }

    _onTabBarClick(event) {
      const tab = event.target.closest('[data-collection-tabs-tab]');

      if (!tab) {
        return;
      }

      event.preventDefault();
      this.selectTab(tab.getAttribute('data-collection-tabs-tab'));
    }

    _onTabBarKeyDown(event) {
      const tab = event.target.closest('[data-collection-tabs-tab]');

      if (!tab || this.tabs.length < 2) {
        return;
      }

      const currentIndex = this.tabs.indexOf(tab);
      let nextIndex = null;

      switch (event.key) {
        case 'ArrowLeft':
          nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
          break;
        case 'ArrowRight':
          nextIndex = (currentIndex + 1) % this.tabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = this.tabs.length - 1;
          break;
      }

      if (nextIndex === null) {
        return;
      }

      event.preventDefault();
      this.selectTab(this.tabs[nextIndex].getAttribute('data-collection-tabs-tab'), { focus: true });
    }

    _onBlockSelect(event) {
      const tab = this.querySelector(`[data-collection-tabs-tab="${event.detail.blockId}"]`);

      if (tab) {
        this.selectTab(event.detail.blockId);
      }
    }

    /**
     * ---------------------------------------------------------------------------------------------------
     * SCROLLER
     * ---------------------------------------------------------------------------------------------------
     */

    _scroll(scrollerElement, direction) {
      const scrollerInner = scrollerElement.querySelector('.scroller__inner');

      if (!scrollerInner) {
        return;
      }

      const amount = scrollerInner.clientWidth * (direction === 'previous' ? -1 : 1);

      // No `behavior` on purpose: it falls back to the CSS `scroll-behavior`, which is smooth except
      // when the visitor asked for reduced motion.
      if (typeof scrollerInner.scrollBy === 'function') {
        scrollerInner.scrollBy({ left: amount });
      } else {
        scrollerInner.scrollLeft += amount;
      }

      window.requestAnimationFrame(this._refreshArrows);
    }

    _refreshArrows() {
      this.querySelectorAll('[data-collection-tabs-scroller]').forEach((scrollerElement) => {
        const scrollerInner = scrollerElement.querySelector('.scroller__inner');
        const previousButton = scrollerElement.querySelector('[data-collection-tabs-scroll="previous"]');
        const nextButton = scrollerElement.querySelector('[data-collection-tabs-scroll="next"]');

        if (!scrollerInner || !previousButton || !nextButton) {
          return;
        }

        const scrollLeft = Math.abs(scrollerInner.scrollLeft);
        const maxScrollLeft = scrollerInner.scrollWidth - scrollerInner.clientWidth;

        previousButton.disabled = scrollLeft <= 1;
        nextButton.disabled = scrollLeft >= maxScrollLeft - 1;
      });
    }

    /**
     * ---------------------------------------------------------------------------------------------------
     * DELEGATED ACTIONS
     * ---------------------------------------------------------------------------------------------------
     */

    _onClick(event) {
      const scrollButton = event.target.closest('[data-collection-tabs-scroll]');

      if (scrollButton) {
        event.preventDefault();
        this._scroll(scrollButton.closest('[data-collection-tabs-scroller]'), scrollButton.getAttribute('data-collection-tabs-scroll'));
        return;
      }

      const addToCartButton = event.target.closest('[data-action="add-to-cart"]');

      if (addToCartButton) {
        this._addToCart(event, addToCartButton);
        return;
      }

      const quickViewButton = event.target.closest('[data-secondary-action="open-quick-view"]');

      if (quickViewButton) {
        this._openQuickView(quickViewButton);
      }
    }

    /**
     * The quick buy buttons are plain submit buttons, so the submission is intercepted too for keyboard
     * users who validate the form with the enter key.
     */
    _onSubmit(event) {
      const formElement = event.target.closest('form[action*="/cart/add"]');

      if (!formElement || window.theme.cartType === 'page') {
        return;
      }

      const submitButton = formElement.querySelector('[data-action="add-to-cart"]');

      if (submitButton) {
        this._addToCart(event, submitButton);
      }
    }

    /**
     * Mirrors FeaturedCollectionSection#_addToCart so the mini cart and the loading bar react the same
     * way they do for the other product lists of the theme.
     */
    _addToCart(event, target) {
      if (window.theme.cartType === 'page') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const formElement = target.closest('form[action*="/cart/add"]');

      if (!formElement) {
        return;
      }

      const body = {};

      new FormData(formElement).forEach((value, key) => {
        body[key] = value;
      });

      target.setAttribute('disabled', 'disabled');
      document.dispatchEvent(new CustomEvent('theme:loading:start'));

      fetch(`${window.routes.cartAddUrl}.js`, {
        body: JSON.stringify(body),
        credentials: 'same-origin',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      }).then((response) => {
        target.removeAttribute('disabled');

        if (response.ok) {
          this.dispatchEvent(new CustomEvent('product:added', {
            bubbles: true,
            detail: {
              button: target,
              variant: null,
              quantity: parseInt(formElement.querySelector('[name="quantity"]').value)
            }
          }));
        } else {
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
        }
      });
    }

    /**
     * Mirrors FeaturedCollectionSection#_openQuickView. The global ModalManager still opens and closes
     * the modal from the button's own data-action="open-modal"; this only fills it in.
     *
     * The theme's ProductSection class is not reachable from here, so the injected markup is booted by
     * dispatching the standard Shopify section lifecycle events, which the theme's SectionContainer
     * already listens for. Without that, the variant pickers inside the modal would be inert.
     */
    _openQuickView(target) {
      const productUrl = new URL(`${window.location.origin}${target.getAttribute('data-product-url')}`);

      // Same breakpoint the theme uses: below 1024px the quick view goes to the product page instead.
      if (window.matchMedia('screen and (max-width: 1023px)').matches) {
        window.location.href = productUrl.href;
        return;
      }

      const modal = document.getElementById(target.getAttribute('aria-controls'));

      if (!modal) {
        return;
      }

      const modalInner = modal.querySelector('.modal__inner');

      modal.classList.add('is-loading');
      productUrl.searchParams.set('view', 'quick-view');

      fetch(productUrl.href, { credentials: 'same-origin', method: 'GET' })
        .then((response) => response.text())
        .then((content) => {
          modalInner.innerHTML = content;
          modal.classList.remove('is-loading');

          const quickViewSection = modalInner.querySelector('[data-section-id]');

          if (!quickViewSection) {
            return;
          }

          const sectionId = quickViewSection.getAttribute('data-section-id');

          modal.dispatchEvent(new CustomEvent('shopify:section:load', {
            bubbles: true,
            detail: { sectionId: sectionId }
          }));

          const doCleanUp = () => {
            modal.dispatchEvent(new CustomEvent('shopify:section:unload', {
              bubbles: true,
              detail: { sectionId: sectionId }
            }));

            modalInner.innerHTML = '';
            modal.removeEventListener('modal:closed', doCleanUp);
          };

          modal.addEventListener('modal:closed', doCleanUp);
        })
        .catch(() => {
          modal.classList.remove('is-loading');
        });
    }

    /**
     * ---------------------------------------------------------------------------------------------------
     * PRODUCT ITEM COLOR SWATCH
     *
     * Mirrors the theme's ProductItemColorSwatch, which is scoped to the sections registered in
     * theme.js and so does not reach this one.
     * ---------------------------------------------------------------------------------------------------
     */

    _onColorSwatchChange(event) {
      const target = event.target.closest('.product-item__swatch-list .color-swatch__radio');

      if (!target) {
        return;
      }

      const productItem = target.closest('.product-item');
      const variantUrl = target.getAttribute('data-variant-url');

      productItem.querySelector('.product-item__image-wrapper').setAttribute('href', variantUrl);
      productItem.querySelector('.product-item__title').setAttribute('href', variantUrl);

      const originalImageElement = productItem.querySelector('.product-item__primary-image');

      if (
        originalImageElement &&
        target.hasAttribute('data-image-url') &&
        target.getAttribute('data-media-id') !== originalImageElement.getAttribute('data-media-id')
      ) {
        const newImageElement = document.createElement('img');

        newImageElement.className = 'product-item__primary-image';
        newImageElement.setAttribute('data-media-id', target.getAttribute('data-media-id'));
        newImageElement.setAttribute('src', target.getAttribute('data-image-url'));

        originalImageElement.parentNode.style.paddingBottom = `${100 / target.getAttribute('data-image-aspect-ratio')}%`;
        originalImageElement.parentNode.replaceChild(newImageElement, originalImageElement);
      }
    }
  }

  window.customElements.define('collection-tabs', CollectionTabs);
})();
